/**
 * Compact subscription card for the org workspace (Overview + Subscription
 * sections).
 *
 * Rolls the two ways our mirror can drift from Stripe — webhook status vs
 * live status, and bundle assignments vs subscription items — into one
 * "Stripe health" signal. The Sync action only renders when there is
 * something to reconcile. Subscription lifecycle actions (extend trial,
 * cancel, reactivate, create) live in the card footer.
 */

import { Sync } from '@mui/icons-material';
import { Box, Button, Chip, CircularProgress, Tooltip } from '@mui/material';

import type { AdminOrganizationDetailResponse } from '@store/generated/organizations';

import { formatDateTime } from '../utils/formatting';
import { describeChange } from './bundle-sync-indicator';
import { useGetBundleSyncPreviewQuery } from '../store/hooks';

/** Display buckets the passport chip + card status understand. */
export type SubscriptionState =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'paused'
  | 'canceled'
  | 'pending_cancellation'
  | 'none';

type SubscriptionDetails = { [key: string]: any } | null | undefined;

export function toSubscriptionState(details: SubscriptionDetails): SubscriptionState {
  const status: string | undefined = details?.status;
  if (!status) return 'none';
  if (details?.cancel_at_period_end) return 'pending_cancellation';
  switch (status) {
    case 'active':
      return 'active';
    case 'trialing':
      return 'trialing';
    case 'past_due':
    case 'unpaid':
    case 'incomplete':
      return 'past_due';
    case 'paused':
      return 'paused';
    case 'canceled':
    case 'incomplete_expired':
      return 'canceled';
    default:
      return 'past_due';
  }
}

const epochDate = (seconds: number | null | undefined) =>
  seconds ? formatDateTime(new Date(seconds * 1000)) : '—';

const WALLET_LABELS: Record<string, string> = {
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  link: 'Link',
};

/** "Visa •••• 4242", "Test Bank •••• 6789", or the raw type as a fallback. */
function describePaymentMethod(pm: { [key: string]: any }): string {
  const name = pm.brand ?? pm.bank_name ?? pm.type ?? 'Unknown';
  const label = String(name).charAt(0).toUpperCase() + String(name).slice(1);
  const wallet = pm.wallet ? ` (${WALLET_LABELS[pm.wallet] ?? pm.wallet})` : '';
  return pm.last4 ? `${label} •••• ${pm.last4}${wallet}` : `${label}${wallet}`;
}

function isCardExpired(pm: { [key: string]: any }): boolean {
  if (!pm.exp_year || !pm.exp_month) return false;
  const now = new Date();
  return (
    pm.exp_year < now.getFullYear() ||
    (pm.exp_year === now.getFullYear() && pm.exp_month < now.getMonth() + 1)
  );
}

interface OrganizationSubscriptionCardProps {
  organization: AdminOrganizationDetailResponse;
  organizationId: string;
  onSync: () => void;
  isSyncing: boolean;
  onExtendTrial: () => void;
  onReactivate: () => void;
  isReactivating: boolean;
  onCreateSubscription: () => void;
}

export function OrganizationSubscriptionCard({
  organization,
  organizationId,
  onSync,
  isSyncing,
  onExtendTrial,
  onReactivate,
  isReactivating,
  onCreateSubscription,
}: OrganizationSubscriptionCardProps) {
  const details = organization.stripe_subscription_details;
  const status: string | undefined = details?.status;

  const { data: bundlePreview, isLoading: bundlePreviewLoading } = useGetBundleSyncPreviewQuery(
    { organizationId },
    { skip: !organizationId || !organization.stripe_subscription_id },
  );

  // Stripe health: live status (Stripe) vs webhook-mirrored status (JotPsych),
  // plus the bundle-assignment dry run. Either drifting => out of sync.
  const webhookStatus = organization.stripe_status_from_webhook;
  const statusMismatch = !!status && !!webhookStatus && status !== webhookStatus;
  const bundleChanges =
    bundlePreview && bundlePreview.success !== false ? (bundlePreview.changes ?? []) : [];
  const outOfSync = statusMismatch || bundleChanges.length > 0;

  if (!details) {
    return (
      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900">Subscription</h3>
        </div>
        <p className="text-sm text-gray-500">
          {organization.stripe_subscription_id
            ? 'No subscription details available from Stripe.'
            : 'This organization has no Stripe subscription.'}
        </p>
      </section>
    );
  }

  const trialDaysLeft = details.trial_end
    ? Math.ceil((details.trial_end * 1000 - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const showCreate = status === 'canceled' || status === 'paused';

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900">Subscription</h3>
        {bundlePreviewLoading ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <CircularProgress size={12} />
            <span className="text-xs text-gray-500">Checking sync…</span>
          </Box>
        ) : outOfSync ? (
          <Chip label="Out of sync" color="warning" size="small" />
        ) : (
          <Tooltip title="JotPsych matches Stripe — status and bundle assignments agree">
            <Chip label="In sync with Stripe" color="success" size="small" variant="outlined" />
          </Tooltip>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
        {details.trial_end && (
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">Trial</dt>
            <dd className="mt-0.5">
              {epochDate(details.trial_start)} – {epochDate(details.trial_end)}
            </dd>
            {status === 'trialing' && trialDaysLeft != null && (
              <dd className="text-xs text-gray-500">
                {trialDaysLeft > 0 ? `${trialDaysLeft} days remaining` : 'Trial ended'}
              </dd>
            )}
          </div>
        )}
        {details.current_period_start && (
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">Billing period</dt>
            <dd className="mt-0.5">
              {epochDate(details.current_period_start)} – {epochDate(details.current_period_end)}
            </dd>
          </div>
        )}
        {'payment_method' in details && (
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-400">Payment method</dt>
            {details.payment_method ? (
              <>
                <dd className="mt-0.5">{describePaymentMethod(details.payment_method)}</dd>
                {details.payment_method.exp_month && details.payment_method.exp_year && (
                  <dd
                    className={`text-xs ${
                      isCardExpired(details.payment_method) ? 'text-red-600' : 'text-gray-500'
                    }`}
                  >
                    {isCardExpired(details.payment_method) ? 'Expired' : 'Expires'}{' '}
                    {details.payment_method.exp_month}/{details.payment_method.exp_year}
                  </dd>
                )}
              </>
            ) : (
              <dd className="mt-0.5 font-medium text-amber-700">None on file</dd>
            )}
          </div>
        )}
      </dl>

      {outOfSync && (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <div className="min-w-0">
            {statusMismatch && (
              <div>
                <span className="font-semibold">Stripe:</span>{' '}
                <span className="capitalize">{status}</span>
                <span className="mx-2 text-amber-300">|</span>
                <span className="font-semibold">JotPsych:</span>{' '}
                <span className="capitalize">{webhookStatus}</span>
              </div>
            )}
            {bundleChanges.length > 0 && (
              <ul className="list-inside list-disc text-xs text-amber-800">
                {bundleChanges.map((c, i) => (
                  <li key={`${c.bundle_id}-${c.action}-${i}`}>{describeChange(c)}</li>
                ))}
              </ul>
            )}
          </div>
          <span className="flex-1" />
          <Button
            size="small"
            variant="contained"
            color="warning"
            startIcon={isSyncing ? <CircularProgress size={14} color="inherit" /> : <Sync />}
            disabled={isSyncing}
            onClick={onSync}
          >
            {isSyncing ? 'Syncing…' : 'Sync from Stripe'}
          </Button>
        </div>
      )}

      {details.cancel_at_period_end && (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm text-amber-800">
          <span>Scheduled to cancel at period end ({epochDate(details.current_period_end)}).</span>
          <Button
            size="small"
            variant="outlined"
            onClick={onReactivate}
            disabled={isReactivating}
            startIcon={isReactivating ? <CircularProgress size={14} /> : null}
          >
            {isReactivating ? 'Reactivating…' : 'Reactivate'}
          </Button>
        </div>
      )}

      {(status === 'trialing' || showCreate) && (
        <div className="mt-3 flex gap-2 border-t border-gray-100 pt-3">
          {status === 'trialing' && (
            <Button size="small" variant="outlined" onClick={onExtendTrial}>
              Extend trial
            </Button>
          )}
          {showCreate && (
            <Button size="small" variant="outlined" onClick={onCreateSubscription}>
              Create new subscription
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
